import { useGlobalLoadingStore } from '~/composables/stores/GlobalLoadingStore';

/**
 * Wraps the native `fetch` (which ofetch / `$fetch` and vue-query all use under the hood) to
 * maintain a global in-flight request counter. A blocking overlay (components/GlobalLoadingOverlay.vue)
 * is shown while the counter is > 0, i.e. while any tracked API request is pending.
 *
 * Only requests to the app's configured API bases are tracked (so Nuxt asset/HMR traffic is ignored).
 * All methods are counted by default — including GET reads — EXCEPT GET requests whose URL matches
 * one of `excludedGetUrlPatterns`. Use that list to opt specific reads (e.g. vue-query background
 * refetches / polling) out of the blocking overlay.
 */
export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  const loading = useGlobalLoadingStore();

  // collect every configured backend base so we only count real API calls
  const apiBases = [
    config.public.apiBase,
    config.public.legacyApiBase,
    config.public.adminApiBase,
    config.public.adminToolsApiBase,
  ].filter((b): b is string => typeof b === 'string' && b.length > 0);

  // exceptions: GET requests whose URL matches any of these patterns do NOT show the overlay.
  // Use this to opt specific reads (e.g. background refetches / polling) out of blocking.
  const excludedGetUrlPatterns: RegExp[] = [
    // GET /api/v1/applications/chat/notification (chat notification polling)
    /\/api\/v1\/applications\/chat\/notification(?:[?#]|$)/i,
    // GET /api/v1/versions/latest (version check polling)
    /\/api\/v1\/versions\/latest(?:[?#]|$)/i,
    // GET /api/v1/applications/{id}/chat-history (chat history fetch/refetch)
    /\/api\/v1\/applications\/[^/]+\/chat-history(?:[?#]|$)/i,
    // GET /api/v1/applications/{id}/chat/count/unread (unread chat count polling)
    /\/api\/v1\/applications\/[^/]+\/chat\/count\/unread(?:[?#]|$)/i,
  ];

  const getUrl = (input: RequestInfo | URL): string => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input.url;
  };

  const getMethod = (input: RequestInfo | URL, init?: RequestInit): string => {
    // init.method wins (ofetch/$fetch passes it there); otherwise a Request object carries its own
    const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
    return method.toUpperCase();
  };

  // Requests carrying this header (SAMMI agent tool calls, utils/agent-tool-executor.ts)
  // never show the blocking overlay — the chat window has its own progress indicators.
  const hasAgentHeader = (input: RequestInfo | URL, init?: RequestInit): boolean => {
    const headers = init?.headers ?? (input instanceof Request ? input.headers : undefined);
    if (!headers) return false;
    if (headers instanceof Headers) return headers.has('x-sammi-agent');
    if (Array.isArray(headers)) return headers.some(([name]) => name.toLowerCase() === 'x-sammi-agent');
    return Object.keys(headers).some((name) => name.toLowerCase() === 'x-sammi-agent');
  };

  const isTracked = (url: string) => apiBases.some((base) => url.startsWith(base) || url.includes(base));

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = getUrl(input);
    const method = getMethod(input, init);
    // count every call to our API, except SAMMI agent calls and GETs excluded via the patterns above
    const tracked =
      isTracked(url) &&
      !hasAgentHeader(input, init) &&
      !(method === 'GET' && excludedGetUrlPatterns.some((pattern) => pattern.test(url)));
    if (tracked) queueMicrotask(() => loading.start());
    try {
      return await originalFetch(input, init);
    } finally {
      if (tracked) queueMicrotask(() => loading.done());
    }
  };
});
